import { PlatformData } from '../../types/performance.types';
import MarginCounter from '../spend-card/margin-counter';
import { setPlatformMargin } from '../../utils/platform-margin';
import { formatCompactNumber, formatPlatformDisplayName } from './overview-beta-format';
import { cn } from 'src/lib/utils';

interface OverviewBetaPlatformStripProps {
  platforms: Record<string, PlatformData>;
  editMode: boolean;
  onPlatformChange: (platform: PlatformData) => void;
}

export function OverviewBetaPlatformStrip({
  platforms,
  editMode,
  onPlatformChange,
}: OverviewBetaPlatformStripProps) {
  const list = Object.values(platforms);

  if (!list.length) {
    return null;
  }

  return (
    <div className="mb-5 flex flex-wrap gap-2">
      {list.map((platform) => {
        const spend = Number(platform.aggregateData?.spend ?? 0) * (1 + platform.margin / 100);
        return (
          <button
            key={platform.name}
            type="button"
            onClick={() =>
              onPlatformChange({
                ...platform,
                enabled: !platform.enabled,
              })
            }
            className={cn(
              'min-w-[140px] flex-1 rounded-xl border px-3.5 py-3 text-left transition-all',
              platform.enabled
                ? 'border-transparent text-white shadow-[0_2px_8px_rgba(13,15,26,0.08)]'
                : 'border-[#D9DBE3] bg-white text-[#8A8EA0] opacity-70'
            )}
            style={platform.enabled ? { backgroundColor: platform.chartColor } : undefined}
          >
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className={cn('text-[12px] font-bold capitalize', !platform.enabled && 'line-through')}>
                {formatPlatformDisplayName(platform.displayName || platform.name)}
              </span>
              {platform.enabled && platform.margin !== 0 ? (
                <span className="rounded-full bg-black/25 px-1.5 py-0.5 text-[10px] font-bold">
                  {platform.margin}
                  %
                </span>
              ) : null}
            </div>
            {editMode ? (
              <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                role="presentation"
              >
                <MarginCounter
                  value={platform.margin}
                  min={-100}
                  max={100}
                  onChange={(value) => {
                    setPlatformMargin(platform.name, value);
                    onPlatformChange({ ...platform, margin: value });
                  }}
                  className="text-white"
                />
              </div>
            ) : (
              <div className="text-[18px] font-bold tracking-[-0.4px]">
                {formatCompactNumber(spend, { currency: true })}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
